import {
  isValidFacebook,
  isValidInstagram,
  isValidPinterest,
  isValidTiktok,
  isValidTripAdvisor,
  isValidUrl,
  isValidX,
  isValidYoutube,
} from "./urlValidators";

enum SocialDomain {
  instagram = "https://www.instagram.com/",
  facebook = "https://www.facebook.com/",
  pinterest = "https://www.pinterest.com/",
  tiktok = "https://www.tiktok.com/@",
  tripadvisor = "https://www.tripadvisor.ca/",
  youtube = "https://www.youtube.com/",
  x = "https://x.com/",
}

const normalizeSocialInput = (
  input: string,
  platform: keyof typeof SocialDomain,
) => {
  const baseUrl =
    SocialDomain[platform.toLowerCase() as keyof typeof SocialDomain];
  if (!baseUrl) throw new Error(`Unsupported platform: ${platform}`);

  input = input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "");

  // Strip known domains (e.g., twitter.com/, youtube.com/, x.com/, etc.)
  const parts = input.split("/");
  const firstPart = parts[0].toLowerCase();

  // Known valid and weird domains from our database
  const knownDomains = [
    "instagram.com",
    "insta.com",
    "nstagram.com",
    "ig.com",
    "fb.com",
    "facebook.com",
    "myfb.com",
    "m.facebook.com",
    "tiktok.com",
    "tiktok.ca",
    "vm.tiktok.com",
    "mytiktok.com",
    "pinterest.com",
    "pinterest.ca",
    "pt.com",
    "pt.ca",
    "pin.it",
    "tripadvisor.com",
    "tripadvisor.ca",
    "ta.com",
    "youtube.com",
    "youtube.ca",
    "youtu.be",
    "m.youtube.com",
    "twitter.com",
    "mobile.twitter.com",
    "x.com",
  ];
  if (knownDomains.includes(firstPart)) {
    parts.shift(); // remove domain
  }

  let path = parts.join("/");

  // Remove domain-like prefix if there's a slash
  const hasSlash = input.includes("/");
  const hasPeriodBeforeSlash =
    hasSlash &&
    input.indexOf(".") > -1 &&
    input.indexOf(".") < input.indexOf("/");
  if (hasSlash && hasPeriodBeforeSlash) {
    const segments = input.split("/");
    path = segments.slice(1).join("/"); // drop everything before first slash
  } else if (
    input.startsWith("@") &&
    (platform === "x" || platform === "tiktok")
  ) {
    // strip @ from Twitter/X and tiktok handles (the URL for tiktok will re-add it and it isn't needed in X)
    path = input.slice(1);
  }

  return `${baseUrl}${path}`;
};

const extractHostAndPath = (value: string) => {
  try {
    const url = new URL(value);
    return (
      url.hostname +
      (url.pathname.length > 1 ? "/" + url.pathname.substring(1) : "")
    );
  } catch {
    return value.trim().replace(/^https?:\/\//i, "");
  }
};

export const normalizeUrl = (value: string) => {
  if (isValidUrl(value)) return value;
  return `https://${extractHostAndPath(value)}`;
};

export const normalizeFacebook = (value: string) => {
  if (isValidFacebook(value)) return value;
  return normalizeSocialInput(value, "facebook");
};

export const normalizeInstagram = (value: string) => {
  if (isValidInstagram(value)) return value;
  return normalizeSocialInput(value, "instagram");
};

export const normalizeX = (value: string) => {
  if (isValidX(value)) return value;
  return normalizeSocialInput(value, "x");
};

export const normalizeYoutube = (value: string) => {
  if (isValidYoutube(value)) return value;
  return normalizeSocialInput(value, "youtube");
};

export const normalizeTiktok = (value: string) => {
  if (isValidTiktok(value)) return value;
  return normalizeSocialInput(value, "tiktok");
};

export const normalizePinterest = (value: string) => {
  if (isValidPinterest(value)) return value;
  return normalizeSocialInput(value, "pinterest");
};

export const normalizeTripAdvisor = (value: string) => {
  if (isValidTripAdvisor(value)) return value;
  return normalizeSocialInput(value, "tripadvisor");
};
